import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Hero — opening manuscript plate.
 *
 * The uploaded engraving sits as a contained editorial figure on the
 * right side of the page. The PNG is rendered EXACTLY as provided:
 *   – no CSS filter (no invert, no sepia, no contrast)
 *   – no blend-mode overlay
 *   – no dark gradients
 *   – no opacity gymnastics on the artwork itself
 *
 * Text lives on the LEFT on pure pearl so the typography reads cleanly.
 */

const HERO_PLATE =
  "https://customer-assets.emergentagent.com/job_heritage-blend-4/artifacts/ffq0mecf_67b88d0eff38c920b27705cfe309d452915c7050ae5153a9c74746257e2bb9eb.png";

export default function Hero() {
  const { t } = useI18n();

  return (
    <section
      data-testid="hero"
      className="relative w-full bg-pearl text-tobacco pt-24 md:pt-28 pb-12 md:pb-16 overflow-hidden"
    >
      <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12 items-center min-h-[88vh]">
        {/* LEFT — typography on pure pearl */}
        <div className="col-span-12 md:col-span-6 lg:col-span-5 flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.4 }}
            className="font-mono text-[10px] md:text-[10.5px] tracking-widest2 uppercase text-tobacco font-medium mb-7"
          >
            {t("hero.eyebrow")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.55 }}
            className="font-display text-tobacco leading-[0.94] tracking-tight"
          >
            <span className="block text-[58px] md:text-[72px] lg:text-[88px] font-normal">
              {t("hero.title_a")}
            </span>
            <span className="block text-[58px] md:text-[72px] lg:text-[88px] italic text-tobacco/90 -mt-1">
              {t("hero.title_b")}
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.95 }}
            className="mt-8"
          >
            <span className="block w-14 h-px bg-tobacco/45" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.05 }}
            className="mt-7 font-mono text-[10.5px] md:text-[11px] tracking-widest2 uppercase text-tobacco/85 font-medium"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.2 }}
            className="mt-6 text-tobacco/85 font-normal text-[15px] md:text-[15.5px] leading-[1.65] max-w-[44ch]"
          >
            {t("hero.description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.4 }}
            className="mt-10 flex flex-col items-start gap-3"
          >
            <button
              type="button"
              onClick={() =>
                document.getElementById("heritage")?.scrollIntoView({ behavior: "smooth" })
              }
              data-testid="hero-cta-primary"
              className="btn-luxe-solid"
            >
              {t("hero.cta_primary")}
              <span className="arrow-tick" />
            </button>
            <button
              type="button"
              onClick={() =>
                document.getElementById("archive")?.scrollIntoView({ behavior: "smooth" })
              }
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 font-mono text-[10.5px] tracking-widest2 uppercase text-tobacco hover:text-tobacco/70 py-2 transition-colors duration-700"
            >
              {t("hero.cta_secondary")}
              <span className="arrow-tick" />
            </button>
          </motion.div>
        </div>

        {/* RIGHT — engraving used 1:1, no filters, no blend modes */}
        <div className="col-span-12 md:col-span-6 lg:col-span-7 mt-8 md:mt-0">
          <motion.figure
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.7 }}
            className="w-full"
            data-testid="hero-plate-figure"
          >
            <img
              src={HERO_PLATE}
              alt={
                t("hero.eyebrow") +
                " — tobacco field at the foot of the Cordillera Central"
              }
              data-testid="hero-plate"
              loading="eager"
              decoding="async"
              className="w-full h-auto block select-none pointer-events-none"
              style={{
                maxHeight: "clamp(360px, 56vw, 640px)",
                objectFit: "contain",
              }}
            />
          </motion.figure>
        </div>
      </div>

      {/* Bottom folio rule — Plate · I · MMXLVIII */}
      <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-12 mt-2">
        <div className="border-t border-tobacco/20 pt-4 flex items-baseline justify-between font-mono text-[10px] tracking-widest2 uppercase text-tobacco/65">
          <span>Plate · I</span>
          <span className="hidden md:inline italic font-display text-[14px] text-tobacco/80 normal-case tracking-normal">
            San Juan de la Maguana · Las Matas de Farfán · Elías Piña
          </span>
          <span>MMXLVIII</span>
        </div>
      </div>
    </section>
  );
}
